import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaSpinner, FaSync, FaCheck, FaTimes, FaHistory, FaUserCircle } from 'react-icons/fa';
import ObjectiveSelector from '../../components/socialContent/ObjectiveSelector';
import ProposalViewer from '../../components/socialContent/ProposalViewer';
import ProductSelector from '../../components/socialContent/ProductSelector';
import TemplateCard from '../../components/socialContent/TemplateCard';
import VariablesForm from '../../components/socialContent/VariablesForm';
import RenderStatus from '../../components/socialContent/RenderStatus';
import TemplateModal from '../../components/socialContent/TemplateModal';
import useAuthStore from '../../store/authStore';
import { ContentObjective } from '../../types/socialContent';
import { get } from '../../helpers/apiHelper';
import {
    generateProposal,
    generateWeeklyPosts,
    getTemplates,
    createPost,
    renderPost
} from '../../helpers/socialContentService';

const SocialContentPage = () => {
    const [activeTab, setActiveTab] = useState('auto');
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPost, setCurrentPost] = useState(null);
    const [renderedImage, setRenderedImage] = useState(null);
    const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
    const [objective, setObjective] = useState(ContentObjective.SALES);
    const [proposal, setProposal] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [generatingProposal, setGeneratingProposal] = useState(false);
    const [userMarca, setUserMarca] = useState(null);
    const [hasBuyerPersona, setHasBuyerPersona] = useState(false);
    const [checkingBuyerPersona, setCheckingBuyerPersona] = useState(true);

    const navigate = useNavigate();
    const { user } = useAuthStore();

    useEffect(() => {
        loadTemplates();
        loadUserMarca();
        checkBuyerPersona();
    }, []);

    const loadUserMarca = async () => {
        try {
            // Si el usuario ya tiene una marca asignada
            if (user.marcaId) {
                const marca = await get(`marcas/${user.marcaId}`);
                setUserMarca(marca);
                return;
            }

            // Si no, obtener la primera marca del usuario
            const marcas = await get('marcas');
            if (marcas && marcas.length > 0) {
                setUserMarca(marcas[0]);
            }
        } catch (err) {
            setError('Error al cargar la información de la marca. Por favor, recarga la página.');
        }
    };

    const loadTemplates = async () => {
        try {
            setLoading(true);
            const data = await getTemplates();
            setTemplates(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const checkBuyerPersona = async () => {
        try {
            setCheckingBuyerPersona(true);
            // Usamos el endpoint correcto para verificar buyer persona
            const response = await get(`buyer-persona/result`);
            // Si la respuesta tiene datos, significa que existe un buyer persona
            setHasBuyerPersona(!!response && Object.keys(response).length > 0);
        } catch (err) {
            console.error('Error checking buyer persona:', err);
            setHasBuyerPersona(false);
        } finally {
            setCheckingBuyerPersona(false);
        }
    };

    const handleGenerateProposal = async () => {
        try {
            setGeneratingProposal(true);
            setError(null);

            // Primero verificamos si tiene buyer persona
            const buyerPersonaResponse = await get(`buyer-persona/result`);
            if (!buyerPersonaResponse || Object.keys(buyerPersonaResponse).length === 0) {
                setHasBuyerPersona(false);
                return;
            }

            const result = await generateProposal({
                marcaId: userMarca._id,
                productId: selectedProduct,
                objective,
                context: 'Generación de contenido social para redes sociales'
            });

            setProposal(result);
        } catch (err) {
            console.error('Error generating proposal:', err);
            setError(err.message || 'Error al generar la propuesta');
        } finally {
            setGeneratingProposal(false);
        }
    };

    const handleApproveProposal = async (selectedPosts) => {
        if (!proposal || !userMarca?._id) return;

        try {
            setLoading(true);
            setError(null);

            const result = await generateWeeklyPosts({
                marcaId: userMarca._id,
                templateId: proposal.recommendedTemplateId,
                theme: proposal.theme,
                baseVariables: proposal.suggestedVariables,
                variations: selectedPosts.map(index => ({
                    title: proposal.proposals[index].title,
                    description: proposal.proposals[index].description
                }))
            });

            navigate('/social-content/history');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleTemplateSelect = (template) => {
        setSelectedTemplate(template);
        setCurrentPost(null);
        setRenderedImage(null);
    };

    const handleVariablesSubmit = async (variables) => {
        try {
            setError(null);
            const post = await createPost({
                templateId: selectedTemplate._id,
                variables
            });
            setCurrentPost(post);
            await renderPost(post._id);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleRenderComplete = (post) => {
        setRenderedImage(post.imageUrl);
    };

    const handleRenderError = (errorMessage) => {
        setError(errorMessage);
    };

    if (checkingBuyerPersona) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <FaSpinner className="animate-spin text-blue-500 text-4xl" />
            </div>
        );
    }

    if (!hasBuyerPersona) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="bg-white rounded-lg shadow-lg p-6 max-w-2xl mx-auto">
                    <div className="flex items-center justify-center text-6xl text-blue-500 mb-6">
                        <FaUserCircle />
                    </div>
                    <h2 className="text-2xl font-bold text-center text-gray-900 mb-4">
                        Completa tu Buyer Persona
                    </h2>
                    <p className="text-gray-600 text-center mb-6">
                        Para generar contenido personalizado y efectivo, necesitamos conocer mejor a tu audiencia ideal.
                        Completa el cuestionario de Buyer Persona para comenzar.
                    </p>
                    <div className="flex justify-center">
                        <button
                            onClick={() => navigate('/onboarding')}
                            className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                        >
                            Completar Buyer Persona
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!userMarca) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
                    <p>No se encontró una marca configurada. Por favor:</p>
                    <ul className="list-disc ml-6 mt-2">
                        <li>Configura tu marca en el panel de administración</li>
                        <li>Asegúrate de tener al menos una marca creada</li>
                        <li>Contacta con soporte si el problema persiste</li>
                    </ul>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <FaSpinner className="animate-spin text-blue-500 text-4xl" />
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="flex justify-between items-center mb-8">
                <h1 className="text-2xl font-bold text-gray-900">
                    Generador de Contenido Social
                </h1>
                <div className="flex space-x-4">
                    <button
                        onClick={() => navigate('/social-content/history')}
                        className="flex items-center space-x-2 px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                    >
                        <FaHistory />
                        <span>Ver Historial</span>
                    </button>
                    <button
                        onClick={() => setActiveTab('auto')}
                        className={`px-4 py-2 rounded-lg ${activeTab === 'auto'
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-200 text-gray-700'
                            }`}
                    >
                        Pack Semanal
                    </button>
                    <button
                        onClick={() => setActiveTab('manual')}
                        className={`px-4 py-2 rounded-lg ${activeTab === 'manual'
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-200 text-gray-700'
                            }`}
                    >
                        Crear Post
                    </button>
                </div>
            </div>

            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    {error}
                </div>
            )}

            {activeTab === 'auto' ? (
                <div className="space-y-6">
                    {!proposal ? (
                        <>
                            <ObjectiveSelector
                                value={objective}
                                onChange={setObjective}
                            />

                            <ProductSelector
                                value={selectedProduct}
                                onChange={setSelectedProduct}
                            />

                            <div className="flex justify-center">
                                <button
                                    onClick={handleGenerateProposal}
                                    disabled={generatingProposal}
                                    className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
                                >
                                    {generatingProposal ? (
                                        <>
                                            <FaSpinner className="animate-spin inline mr-2" />
                                            Generando propuestas...
                                        </>
                                    ) : (
                                        'Generar Propuestas'
                                    )}
                                </button>
                            </div>
                        </>
                    ) : (
                        <ProposalViewer
                            proposal={proposal}
                            onApprove={handleApproveProposal}
                        />
                    )}
                </div>
            ) : (
                <>
                    {!selectedTemplate ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {templates.map(template => (
                                <TemplateCard
                                    key={template._id}
                                    template={template}
                                    onSelect={handleTemplateSelect}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="space-y-6">
                                <button
                                    onClick={() => setSelectedTemplate(null)}
                                    className="text-blue-500 hover:text-blue-600"
                                >
                                    ← Volver a plantillas
                                </button>

                                <div className="bg-white rounded-lg shadow p-6">
                                    <h2 className="text-xl font-semibold mb-4">
                                        {selectedTemplate.name}
                                    </h2>
                                    <VariablesForm
                                        variables={selectedTemplate.variables}
                                        onSubmit={handleVariablesSubmit}
                                    />
                                </div>
                            </div>

                            <div className="bg-white rounded-lg shadow p-6">
                                <h2 className="text-xl font-semibold mb-4">
                                    Vista Previa
                                </h2>

                                {currentPost ? (
                                    <div className="space-y-4">
                                        <RenderStatus
                                            postId={currentPost._id}
                                            onComplete={handleRenderComplete}
                                            onError={handleRenderError}
                                        />

                                        {renderedImage && (
                                            <div className="space-y-4">
                                                <img
                                                    src={renderedImage}
                                                    alt="Post renderizado"
                                                    className="w-full rounded-lg shadow"
                                                />
                                                <div className="flex justify-center">
                                                    <a
                                                        href={renderedImage}
                                                        download
                                                        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                                                    >
                                                        Descargar Imagen
                                                    </a>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center h-64 bg-gray-100 rounded-lg">
                                        <p className="text-gray-500">
                                            Completa el formulario para generar el post
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}

            <TemplateModal
                isOpen={isTemplateModalOpen}
                onClose={() => setIsTemplateModalOpen(false)}
                onSuccess={loadTemplates}
            />
        </div>
    );
};

export default SocialContentPage; 